import { useState } from "react";

import { useSendConfirm, type SendConfirm } from "./useSendConfirm";

interface SendNotice {
  tone: "done" | "problem";
  text: string;
}

/** The state of one email's test-then-send steps. */
export interface SendFlow {
  /** What is typed in the "Send a test to" box. */
  testAddress: string;
  /** A test went out since the last change, so sending to everyone is
   *  allowed. */
  testedOk: boolean;
  notice: SendNotice | null;
  confirm: SendConfirm;
  editTestAddress: (value: string) => void;
  /** Call when anything that changes the email is edited (the events it
   *  invites people to, who is ticked, which version): the old test no
   *  longer shows what will go out. */
  optionsChanged: () => void;
  testStarted: () => void;
  testSent: (text: string) => void;
  sendAllStarted: () => void;
  sendAllDone: (text: string) => void;
  problem: (text: string) => void;
}

/**
 * One copy of the rule every send follows: nothing goes to everyone
 * until a test has gone out, and any change after the test (the address,
 * the options) takes the send away again until a fresh test succeeds.
 */
export function useSendFlow(defaultTestAddress: string): SendFlow {
  const [testAddress, setTestAddress] = useState(defaultTestAddress);
  const [testedOk, setTestedOk] = useState(false);
  const [notice, setNotice] = useState<SendNotice | null>(null);
  const confirm = useSendConfirm();

  const needFreshTest = () => {
    setTestedOk(false);
    confirm.disarm();
    setNotice(null);
  };

  return {
    testAddress,
    testedOk,
    notice,
    confirm,
    editTestAddress: (value) => {
      setTestAddress(value);
      needFreshTest();
    },
    optionsChanged: needFreshTest,
    testStarted: () => {
      confirm.disarm();
      setNotice(null);
    },
    testSent: (text) => {
      setTestedOk(true);
      setNotice({ tone: "done", text });
    },
    sendAllStarted: () => setNotice(null),
    sendAllDone: (text) => {
      confirm.disarm();
      setTestedOk(false);
      setNotice({ tone: "done", text });
    },
    problem: (text) => {
      confirm.disarm();
      setNotice({ tone: "problem", text });
    },
  };
}
